/**
 * Jail pixel-art — persistent world-space police-station marker + the
 * full-screen "BUSTED" overlay with countdown and breakout buttons, plus
 * the player's own car drawn sadly behind bars.
 *
 * Extracted from GameCanvas.jsx (iteration 27). Behavior-preserving.
 */

/**
 * Persistent police-station marker drawn every frame at fixed world coords
 * (WORLD_W - 80, 80). Tells the player where they'll be teleported on
 * arrest.
 */
export function drawJailCellMarker(ctx, cx, cy) {
  ctx.save();
  // Building base
  ctx.fillStyle = "#6B4423";
  ctx.fillRect(cx - 44, cy - 36, 88, 72);
  // Brick texture
  for (let r = 0; r < 72; r += 8) {
    for (let c = 0; c < 88; c += 16) {
      const off = (Math.floor(r / 8) % 2) * 8;
      ctx.fillStyle = (r + c) % 4 === 0 ? "#8B5A33" : "#5C3818";
      ctx.fillRect(cx - 44 + c + off, cy - 36 + r, 14, 6);
    }
  }
  // Front cell window
  ctx.fillStyle = "#1d1a24";
  ctx.fillRect(cx - 26, cy - 22, 52, 38);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 2;
  ctx.strokeRect(cx - 26, cy - 22, 52, 38);
  // Bars
  ctx.fillStyle = "#9c98a4";
  for (let i = 0; i < 5; i++) {
    ctx.fillRect(cx - 22 + i * 11, cy - 22, 3, 38);
  }
  // POLICE sign
  ctx.fillStyle = "#1E5BC8";
  ctx.fillRect(cx - 36, cy - 44, 72, 10);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 1;
  ctx.strokeRect(cx - 36, cy - 44, 72, 10);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "bold 7px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("POLICE", cx, cy - 39);
  // Flashing alarm bulbs
  const t = Math.floor(performance.now() / 250) % 2;
  ctx.fillStyle = t === 0 ? "#FF3030" : "#1E90FF";
  ctx.fillRect(cx - 42, cy - 50, 6, 6);
  ctx.fillStyle = t === 0 ? "#1E90FF" : "#FF3030";
  ctx.fillRect(cx + 36, cy - 50, 6, 6);
  // Foundation outline
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 2;
  ctx.strokeRect(cx - 44, cy - 36, 88, 72);
  ctx.restore();
}

/**
 * Full-screen BUSTED overlay — painted during an active jail sentence.
 * Soft vignette + stone cell + iron bars + right-side info panel with
 * countdown and pixel buttons for the two breakout options.
 */
export function drawJailOverlay(ctx, player, secondsLeft, canMonkey = false, canSell = false, car = null) {
  const now = performance.now();

  // Soft vignette
  const grad = ctx.createRadialGradient(640, 410, 200, 640, 410, 760);
  grad.addColorStop(0, "rgba(0,0,0,0.55)");
  grad.addColorStop(1, "rgba(0,0,0,0.92)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1280, 820);

  // Centered cell
  const cellW = 480;
  const cellH = 360;
  const cellX = 200;
  const cellY = 230;

  // Stone wall
  ctx.fillStyle = "#1d1a24";
  ctx.fillRect(cellX, cellY, cellW, cellH);
  for (let r = 0; r < cellH; r += 24) {
    for (let c = 0; c < cellW; c += 48) {
      const off = (Math.floor(r / 24) % 2) * 24;
      ctx.fillStyle = ((r + c) % 16 === 0) ? "#332e3a" : "#28232f";
      ctx.fillRect(cellX + c + off, cellY + r, 46, 22);
    }
  }

  // Cell floor
  ctx.fillStyle = "#3a3340";
  ctx.fillRect(cellX, cellY + cellH - 50, cellW, 50);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 3;
  ctx.strokeRect(cellX, cellY + cellH - 50, cellW, 50);

  if (car) {
    drawJailedCar(ctx, cellX + cellW / 2, cellY + cellH - 90, car);
  }

  // Iron bars
  const barCount = 9;
  const barW = 10;
  const barGap = (cellW - barCount * barW) / (barCount + 1);
  for (let i = 0; i < barCount; i++) {
    const bx = cellX + barGap + i * (barW + barGap);
    ctx.fillStyle = "#9c98a4";
    ctx.fillRect(bx, cellY, barW, cellH);
    ctx.fillStyle = "#cfcad4";
    ctx.fillRect(bx + 2, cellY, 3, cellH);
    ctx.strokeStyle = "#0e0c12";
    ctx.lineWidth = 2;
    ctx.strokeRect(bx, cellY, barW, cellH);
  }
  // Top / bottom rails
  ctx.fillStyle = "#9c98a4";
  ctx.fillRect(cellX, cellY, cellW, 12);
  ctx.fillRect(cellX, cellY + cellH - 12, cellW, 12);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 2;
  ctx.strokeRect(cellX, cellY, cellW, 12);
  ctx.strokeRect(cellX, cellY + cellH - 12, cellW, 12);

  // Right-side info panel
  const panelX = 720;
  const panelY = 230;
  const panelW = 380;
  const panelH = 360;
  ctx.fillStyle = "rgba(20, 20, 24, 0.92)";
  ctx.fillRect(panelX, panelY, panelW, panelH);
  ctx.strokeStyle = "#FFCC00"; ctx.lineWidth = 4;
  ctx.strokeRect(panelX, panelY, panelW, panelH);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 2;
  ctx.strokeRect(panelX - 4, panelY - 4, panelW + 8, panelH + 8);

  // BUSTED headline
  ctx.font = "bold 38px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineJoin = "round";
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 6;
  ctx.strokeText("BUSTED", panelX + panelW / 2, panelY + 50);
  ctx.fillStyle = "#FF3B30";
  ctx.fillText("BUSTED", panelX + panelW / 2, panelY + 50);

  // 7-segment-style countdown
  const cdY = panelY + 130;
  ctx.font = "bold 64px 'Press Start 2P', monospace";
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 8;
  const cdText = `:${String(secondsLeft).padStart(2, "0")}`;
  ctx.strokeText(cdText, panelX + panelW / 2, cdY);
  ctx.fillStyle = "#FFCC00";
  ctx.fillText(cdText, panelX + panelW / 2, cdY);

  ctx.font = "bold 10px 'Press Start 2P', monospace";
  ctx.fillStyle = "#888";
  ctx.fillText("UNTIL RELEASE", panelX + panelW / 2, cdY + 40);

  const pulse = Math.floor(now / 400) % 2 === 0;

  // Monkey button (E key)
  const mBtn = { x: panelX + 30, y: panelY + 215, w: panelW - 60, h: 50 };
  ctx.fillStyle = canMonkey ? (pulse ? "#FFCC00" : "#E5B800") : "#3a3340";
  ctx.fillRect(mBtn.x, mBtn.y, mBtn.w, mBtn.h);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 3;
  ctx.strokeRect(mBtn.x, mBtn.y, mBtn.w, mBtn.h);
  ctx.font = "bold 14px 'Press Start 2P', monospace";
  ctx.fillStyle = canMonkey ? "#0e0c12" : "#666";
  ctx.fillText(canMonkey ? "[E] LAUNCH MONKEY" : "[E] NO MONKEY", mBtn.x + mBtn.w / 2, mBtn.y + mBtn.h / 2);

  // Sell-artifact button
  const sBtn = { x: panelX + 30, y: panelY + 280, w: panelW - 60, h: 50 };
  ctx.fillStyle = canSell ? (pulse ? "#34C759" : "#22A04E") : "#3a3340";
  ctx.fillRect(sBtn.x, sBtn.y, sBtn.w, sBtn.h);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 3;
  ctx.strokeRect(sBtn.x, sBtn.y, sBtn.w, sBtn.h);
  ctx.fillStyle = canSell ? "#0e0c12" : "#666";
  ctx.fillText(canSell ? "SELL ARTIFACT (-1)" : "NOTHING TO SELL", sBtn.x + sBtn.w / 2, sBtn.y + sBtn.h / 2);
}

/**
 * Player's car drawn inside the cell — uses their actual paint colors so
 * they recognise themselves locked up. Small sad face overhead.
 */
export function drawJailedCar(ctx, cx, cy, car) {
  ctx.save();
  ctx.translate(cx, cy);
  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.5)";
  ctx.fillRect(-26, 18, 52, 6);
  // Body
  ctx.fillStyle = car.body_color || "#FF3B30";
  ctx.fillRect(-24, -16, 48, 32);
  ctx.strokeStyle = "#0e0c12"; ctx.lineWidth = 2;
  ctx.strokeRect(-24, -16, 48, 32);
  // Accent stripe
  ctx.fillStyle = car.accent_color || "#FFCC00";
  ctx.fillRect(-24, -2, 48, 4);
  // Windshield
  ctx.fillStyle = "#3E5A7A";
  ctx.fillRect(-14, -12, 28, 6);
  // Wheels
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(-22, -20, 8, 6);
  ctx.fillRect(14, -20, 8, 6);
  ctx.fillRect(-22, 14, 8, 6);
  ctx.fillRect(14, 14, 8, 6);
  // Sad face
  ctx.font = "bold 16px 'Press Start 2P', monospace";
  ctx.textAlign = "center";
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText("·_·", 0, -36);
  ctx.restore();
}
